import { Box, SxProps, Typography } from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import ComercialInterfaceHeader from "../../../../../components/comercial/ComercialInterfaceHeader";
import { IStore } from "../../../../../services/api/Store/types";
import { sharedStyles } from "../../../../../styles/shared";
import ContactIcon from "../../../../../components/general/ContactIcon";
import { getLocalStorageItem } from "../../../../../hooks/localStorageHooks";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import { useTranslation } from "react-i18next";
import ImageGalleryContainerApi
    from "@/components/general/Images/ImageGalleryContainerApi";

const timeToMinutes = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

const minutesToTime = (minutes: number) => {
    const h = Math.floor(minutes / 60).toString().padStart(2, "0");
    const m = (minutes % 60).toString().padStart(2, "0");
    return `${h}:${m}`;
};

const detectInterval = (times: string[]) => {
    if (!times || times.length === 0) return 60;
    if (times.some((t) => t.endsWith(":15") || t.endsWith(":45"))) return 15;
    if (times.some((t) => t.endsWith(":30"))) return 30;
    return 60;
};

const formatDaySchedule = (times: string[], closedLabel: string) => {
    if (!times || times.length === 0) return closedLabel;

    const sortedTimes = [...times].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));

    const interval = detectInterval(sortedTimes);

    const ranges: string[] = [];
    let start = sortedTimes[0];
    let prev = sortedTimes[0];

    for (let i = 1; i < sortedTimes.length; i++) {
        const current = sortedTimes[i];
        const diff = timeToMinutes(current) - timeToMinutes(prev);

        if (diff > interval) {
            const endMinutes = timeToMinutes(prev) + interval;
            ranges.push(`${start} - ${minutesToTime(endMinutes)}`);
            start = current;
        }
        prev = current;
    }

    const lastEndMinutes = timeToMinutes(prev) + interval;
    ranges.push(`${start} - ${minutesToTime(lastEndMinutes)}`);

    return ranges.join("  |  ");
};

const StoreDetails = () => {
  const { t } = useTranslation();
  const selectedStore: IStore = getLocalStorageItem("selectedStore")!;
  const renderStoreData = (
    <>
     <ComercialInterfaceHeader title={t("storePage.management.details.dataTitle")} />

      <Box sx={sharedStyles.verticalContent}>
        <Typography fontWeight="bold" variant="h6" textAlign="center">
          {`${selectedStore.name} (${selectedStore.CNPJ})`}
        </Typography>

        <Box sx={styles.singleContact}>
          <ContactIcon icon={<FmdGoodOutlinedIcon color="primary" />} />
          <Typography>
            {`${selectedStore.logradouro}, ${selectedStore.number || "s/n"} - ${
              selectedStore.district
            }, ${selectedStore.city} - ${selectedStore.state}, ${
              selectedStore.CEP
            }`}
          </Typography>
        </Box>

        <Box sx={styles.singleContact}>
          <ContactIcon icon={<LocalPhoneIcon color="primary" />} />
          <Typography>{selectedStore?.phone}</Typography>
        </Box>
        <Box sx={styles.singleContact}>
          <ContactIcon icon={<AlternateEmailIcon color="primary" />} />
          <Typography>{selectedStore?.email}</Typography>
        </Box>
      </Box>

      <Box>
        <Typography fontWeight="bold">{t("storePage.management.details.descriptionLabel")}</Typography>

        {selectedStore.description?.length > 0 ? (
          <Typography variant="body1" textAlign="justify">
            {selectedStore.description}
          </Typography>
        ) : (
          <Typography variant="body1" textAlign="justify">
                        {t("storePage.management.details.noDescription")}
          </Typography>
        )}
      </Box>
    </>
  );

  const renderImageGallery = (
    <>
      <ComercialInterfaceHeader title={t("storePage.management.details.galleryTitle")} />
        <ImageGalleryContainerApi images={selectedStore.images ?? []} />
    </>
  );

  const weekSchedule = [
        { label: t("timeRange.days.monday"), times: selectedStore.timeMonday },
        { label: t("timeRange.days.tuesday"), times: selectedStore.timeTuesday },
        { label: t("timeRange.days.wednesday"), times: selectedStore.timeWednesday },
        { label: t("timeRange.days.thursday"), times: selectedStore.timeThursday },
        { label: t("timeRange.days.friday"), times: selectedStore.timeFriday },
        { label: t("timeRange.days.saturday"), times: selectedStore.timeSaturday },
        { label: t("timeRange.days.sunday"), times: selectedStore.timeSunday },
    ];

  const closedLabel = t("workingScale.caption.closed");

  const renderWorkScale = (
    <>
      <ComercialInterfaceHeader title={t("storePage.management.details.workingHoursTitle")} />
      <Box sx={{ ...sharedStyles.verticalContent, gap: 1 }}>
                {weekSchedule.map((day) => (
                    <Box
                        key={day.label}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderBottom: "1px solid #eee",
                            paddingBottom: 0.5
                        }}
                    >
                        <Typography fontWeight="bold" color="text.primary">
                            {day.label}:
                        </Typography>
                        <Typography color="text.secondary">
                            {formatDaySchedule(day.times, closedLabel)}
                        </Typography>
                    </Box>
                ))}
      </Box>
    </>
  );

  const renderAddStorePageContent = (
    <>
      <LeftMarginBackButton />

      <div style={sharedStyles.form}>
        {renderStoreData}
        {renderImageGallery}
        {renderWorkScale}
      </div>
    </>
  );

  return (
    <Box sx={sharedStyles.sharedMainContainer}>
      <ComercialDrawer content={renderAddStorePageContent} />
    </Box>
  );
};

export default StoreDetails;

const styles = {
  singleContact: { display: "flex", alignItems: "center", gap: 1 } as SxProps,
};
